import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Badge, ErrorText, Input, Label, Textarea } from '../../components/ui';
import { HomePageTextField, HomeWords } from '../../utils/homePageSettings';
import { textFieldState } from './pageSettingsForm';

const Root = styled.div`
  ${tw`flex flex-col min-w-0 w-full`}
`;

const LabelRow = styled.div`
  ${tw`flex items-center justify-between gap-2 flex-wrap`}
`;

const Meta = styled.div`
  ${tw`flex items-center justify-between gap-2 mt-1.5 text-xs`}
  color: var(--color-text-secondary);
`;

const Count = styled.span<{ $over: boolean }>`
  font-variant-numeric: tabular-nums;
  ${({ $over }) => ($over ? 'color: var(--color-error); font-weight: 600;' : '')}
`;

const LinkButton = styled.button`
  ${tw`text-xs font-medium underline`}
  color: var(--color-text-primary);
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  &:disabled {
    cursor: default;
    color: var(--color-text-tertiary);
    text-decoration: none;
  }
`;

/** One home page text box: counter "n / limit", "Default" tag, "Use default" (spec 029). */
export function HomeTextField({
  field,
  label,
  value,
  defaults,
  multiline,
  serverError,
  disabled,
  onChange,
}: {
  field: HomePageTextField;
  label: string;
  value: string;
  defaults: HomeWords;
  multiline?: boolean;
  serverError?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const state = textFieldState(field, value, defaults);
  const id = `home-text-${field}`;
  const error = state.error ?? serverError ?? null;
  const common = {
    id,
    value,
    disabled,
    'aria-invalid': !!error,
    'aria-describedby': `${id}-meta`,
    'data-testid': id,
  };
  return (
    <Root>
      <LabelRow>
        <Label htmlFor={id}>{label}</Label>
        {state.isDefault && (
          <Badge tone="neutral" size="sm">
            Default
          </Badge>
        )}
      </LabelRow>
      {multiline ? (
        <Textarea
          {...common}
          rows={3}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            // One line on the page: Enter would only become a space.
            if (e.key === 'Enter') e.preventDefault();
          }}
        />
      ) : (
        <Input {...common} type="text" onChange={e => onChange(e.target.value)} />
      )}
      <Meta id={`${id}-meta`}>
        <Count $over={state.count > state.limit} data-testid={`${id}-count`}>
          {state.count} / {state.limit}
        </Count>
        <LinkButton
          type="button"
          disabled={disabled || value === defaults[field]}
          onClick={() => onChange(defaults[field])}
        >
          Use default
        </LinkButton>
      </Meta>
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Root>
  );
}
